/* Whisply slide cart: server-rendered via the Section Rendering API, with auto gifts,
   protection, gift wrap, Rx pairs and upsells. Every change re-renders the drawer. */
const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const OPTOUT = 'whisply_protection_optout';
const WOFF = 'whisply_warranty_off';
const store = { get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } }, set: (k, v) => { try { localStorage.setItem(k, v); } catch (e) {} } };

class WhisplyCart extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
    document.addEventListener('shopify:cart:lines-update', (e) => {
      if (e.detail && e.detail.source === 'whisply-cart') return;
      if (e.action === 'add') store.set(OPTOUT, '');
      (e.promise || Promise.resolve()).then(() => this.refresh()).catch(() => this.refresh());
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    this.reconcile();
  }
  get state() { try { return JSON.parse(this.querySelector('[data-w-cd-state]').textContent); } catch (e) { return null; } }
  open() {
    if (this.classList.contains('is-open')) return;
    const pw = document.querySelector('.page-wrapper');
    this._pos = { w: window.scrollY, p: pw ? pw.scrollTop : 0 };
    this.classList.add('is-open');
    document.documentElement.classList.add('w-cd-lock');
    setTimeout(() => this.querySelector('.w-cd').focus({ preventScroll: true }), 50);
  }
  close() {
    if (!this.classList.contains('is-open')) return;
    const pos = this._pos, pw = document.querySelector('.page-wrapper');
    if (document.activeElement && this.contains(document.activeElement)) document.activeElement.blur();
    this.classList.remove('is-open'); document.documentElement.classList.remove('w-cd-lock');
    if (pos) { const back = () => { if (Math.abs(window.scrollY - pos.w) > 2) window.scrollTo(0, pos.w); if (pw && Math.abs(pw.scrollTop - pos.p) > 2) pw.scrollTop = pos.p; }; back(); requestAnimationFrame(back); setTimeout(back, 60); }
  }

  async refresh(skipReconcile) {
    this.classList.add('is-busy');
    try {
      const html = await (await fetch(`${root}?section_id=${this.section}`)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const body = doc.querySelector('[data-w-cd-body]');
      const openCode = this.querySelector('.w-cd__code[open]');
      const scroll = this.querySelector('.w-cd__scroll');
      const top = scroll ? scroll.scrollTop : 0;
      if (body) this.querySelector('[data-w-cd-body]').innerHTML = body.innerHTML;
      if (openCode) { const c = this.querySelector('.w-cd__code'); if (c) c.open = true; }
      const s2 = this.querySelector('.w-cd__scroll'); if (s2) s2.scrollTop = top;
      const st = this.state;
      if (st) document.querySelectorAll('[data-w-cart-count]').forEach((b) => (b.textContent = st.count));
      document.dispatchEvent(new CustomEvent('whisply:cart', { detail: st }));
    } finally { this.classList.remove('is-busy'); }
    if (!skipReconcile) await this.reconcile();
  }

  async post(path, body) {
    const r = await fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const j = await r.json();
    if (!r.ok) throw new Error(j.description || j.message || 'Cart error');
    return j;
  }

  /* keep gifts, protection, Rx fees and wraps in sync with the cart */
  async reconcile() {
    if (this._rec) return; this._rec = true;
    try {
      const st = this.state; if (!st) return;
      const lines = st.lines, updates = {}, add = [];
      const byId = (id) => lines.filter((l) => l.id === id);
      const has = (id) => byId(id).length > 0;
      const main = lines.filter((l) => !l.fee && !l.wFor && l.price > 0 && l.id !== st.prot);
      // free care kit
      if (st.kit) {
        const want = st.kitWhen === 'eyewear' ? st.eyewear > 0 : main.length > 0;
        byId(st.kit).forEach((l, i) => { if (!want || i > 0) updates[l.key] = 0; else if (l.q !== 1) updates[l.key] = 1; });
        if (want && !has(st.kit)) add.push({ id: st.kit, quantity: 1 });
      }
      // tier gifts
      (st.tiers || []).filter(Boolean).forEach((t) => {
        const want = st.qualified >= t.at && main.length > 0;
        byId(t.id).forEach((l, i) => { if (!want || i > 0) updates[l.key] = 0; else if (l.q !== 1) updates[l.key] = 1; });
        if (want && !has(t.id)) add.push({ id: t.id, quantity: 1 });
      });
      // protection
      if (st.prot) {
        const prot = byId(st.prot);
        if (!main.length) prot.forEach((l) => (updates[l.key] = 0));
        else {
          prot.forEach((l, i) => { if (i > 0) updates[l.key] = 0; else if (l.q !== 1) updates[l.key] = 1; });
          if (st.protAuto && !store.get(OPTOUT) && !prot.length) add.push({ id: st.prot, quantity: 1 });
        }
      }
      // Rx fee follows its glasses; wrap follows its pair
      const sum = (arr) => arr.reduce((a, l) => a + l.q, 0);
      lines.filter((l) => l.fee).forEach((f) => {
        const q = sum(lines.filter((l) => l.pair === f.pair && !l.fee));
        if (f.q !== q) updates[f.key] = q;
      });
      const covers = (key) => lines.filter((l) => !l.fee && !l.wFor && (key.startsWith('rx:') ? l.rx && 'rx:' + l.pair === key : !l.rx && 'v:' + l.id === key));
      lines.filter((l) => l.wFor).forEach((w) => { const q = sum(covers(w.wFor)); if (w.q !== q) updates[w.key] = q; });
      if (st.wAuto) {
        let off = []; try { off = JSON.parse(store.get(WOFF) || '[]'); } catch (e) {}
        const keys = new Set();
        lines.filter((l) => !l.fee && !l.wFor && l.price > 0 && (l.eye || l.rx)).forEach((l) => keys.add(l.rx ? 'rx:' + l.pair : 'v:' + l.id));
        keys.forEach((k) => {
          if (off.includes(k) || lines.some((l) => l.wFor === k)) return;
          const id = k.startsWith('rx:') ? st.wr : st.wb; if (!id) return;
          const g = covers(k), first = g[0];
          add.push({ id, quantity: sum(g), properties: { For: first ? (first.title || '') : '', '_Warranty for': k } });
        });
      }
      let changed = false;
      if (Object.keys(updates).length) { await this.post('cart/update.js', { updates }); changed = true; }
      if (add.length) { try { await this.post('cart/add.js', { items: add }); } catch (e) {} changed = true; }
      if (changed) await this.refresh(true);
    } catch (e) { console.warn('[whisply-cart]', e); }
    finally { this._rec = false; }
  }

  async change(key, quantity) {
    const st = this.state, updates = { [key]: quantity };
    await this.post('cart/update.js', { updates });
    await this.refresh();
  }

  async onClick(e) {
    const t = e.target;
    if (t.closest('[data-w-cd-close]')) { this.close(); return; }
    const q = t.closest('[data-w-cd-qty]');
    if (q) { const li = q.closest('[data-key]'); q.disabled = true; try { await this.change(li.dataset.key, parseInt(q.dataset.wCdQty, 10)); } catch (er) { this.flash(er.message); } return; }
    const a = t.closest('[data-w-cd-add]');
    if (a) {
      a.disabled = true; a.textContent = '…';
      try { await this.post('cart/add.js', { items: [{ id: a.dataset.wCdAdd, quantity: 1 }] }); await this.refresh(); } catch (er) { this.flash(er.message); a.disabled = false; a.textContent = '+ Add'; }
      return;
    }
    const wr = t.closest('[data-w-cd-warr]');
    if (wr) {
      const box = wr.closest('[data-wkey]'), key = box.dataset.wkey, st = this.state;
      const existing = st.lines.filter((l) => l.wFor === key);
      const covered = st.lines.filter((l) => !l.fee && !l.wFor && (key.startsWith('rx:') ? l.rx && 'rx:' + l.pair === key : !l.rx && 'v:' + l.id === key));
      const qty = covered.reduce((a, l) => a + l.q, 0) || 1;
      const li = wr.closest('[data-key]'), name = li.querySelector('.w-cd__name').textContent.trim(), v = li.querySelector('.w-cd__var');
      try {
        if (wr.dataset.wCdWarr === 'on' && !existing.length) await this.post('cart/add.js', { items: [{ id: +box.dataset.wid, quantity: qty, properties: { For: name + (v ? ' – ' + v.textContent.trim() : ''), '_Warranty for': key } }] });
        { let off = []; try { off = JSON.parse(store.get(WOFF) || '[]'); } catch (e) {} off = off.filter((x) => x !== key); if (wr.dataset.wCdWarr === 'off') off.push(key); store.set(WOFF, JSON.stringify(off.slice(-40))); }
        if (wr.dataset.wCdWarr === 'on' && existing.length) {}
        else if (wr.dataset.wCdWarr === 'off' && existing.length) { const updates = {}; existing.forEach((x) => (updates[x.key] = 0)); await this.post('cart/update.js', { updates }); }
      } catch (er) { this.flash(er.message); }
      await this.refresh();
      return;
    }
    const w = t.closest('[data-w-cd-wrap]');
    if (w) {
      const li = w.closest('[data-key]'), st = this.state, key = li.dataset.key;
      const line = st.lines.find((l) => l.key === key);
      const vid = String(line ? line.id : '');
      const existing = st.lines.filter((l) => String(l.wrapFor) === vid);
      const total = st.lines.filter((l) => String(l.id) === vid && !l.wrapFor).reduce((a, l) => a + l.q, 0) || 1;
      if (w.dataset.wCdWrap === 'on' && !existing.length && st.wrap) {
        const name = li.querySelector('.w-cd__name').textContent.trim();
        const v = li.querySelector('.w-cd__var');
        await this.post('cart/add.js', { items: [{ id: st.wrap, quantity: total, properties: { For: name + (v ? ' – ' + v.textContent.trim() : ''), '_Wrap for': vid } }] });
      } else if (w.dataset.wCdWrap === 'off' && existing.length) {
        const updates = {}; existing.forEach((x) => (updates[x.key] = 0));
        await this.post('cart/update.js', { updates });
      }
      await this.refresh();
      return;
    }
    if (t.closest('[data-w-cd-code]')) {
      const input = this.querySelector('[data-w-cd-code-input]'); const code = input.value.trim(); if (!code) return;
      try {
        const cart = await this.post('cart/update.js', { discount: code });
        const ok = (cart.discount_codes || []).some((d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable);
        await this.refresh();
        if (!ok) this.flash('That code isn’t valid for this cart.');
      } catch (er) { this.flash(er.message); }
      return;
    }
    if (t.closest('[data-w-cd-uncode]')) { await this.post('cart/update.js', { discount: '' }); await this.refresh(); }
  }

  async onChange(e) {
    const tg = e.target.closest('[data-w-cd-toggle]');
    if (!tg) return;
    const id = parseInt(tg.dataset.wCdToggle, 10), st = this.state;
    if (tg.dataset.kind === 'prot') store.set(OPTOUT, tg.checked ? '' : '1');
    try {
      if (tg.checked) await this.post('cart/add.js', { items: [{ id, quantity: 1 }] });
      else { const updates = {}; st.lines.filter((l) => l.id === id).forEach((l) => (updates[l.key] = 0)); await this.post('cart/update.js', { updates }); }
    } catch (er) { this.flash(er.message); }
    await this.refresh();
  }

  flash(msg) { if (window.Whisply && window.Whisply.toast) window.Whisply.toast(msg); }
}
if (!customElements.get('whisply-cart')) customElements.define('whisply-cart', WhisplyCart);
